import React, { useState, useRef, useEffect, useCallback } from 'react';
import CanvasPreview from './components/CanvasPreview';
import Timeline from './components/Timeline';
import Inspector from './components/Inspector';
import MediaBin from './components/MediaBin';
import DocsModal from './components/DocsModal';
import { exportVideo } from './utils/exporter';

export default function App() {
  const [audioUrl, setAudioUrl] = useState(null);
  const [bgMedia, setBgMedia] = useState({ type: 'color', url: '', color: '#111827', element: null, filter: 'none' });
  const [lyrics, setLyrics] = useState([]);
  const [styles, setStyles] = useState({
    fontFamily: 'Inter',
    fontSize: 64,
    textColor: '#ffffff',
    activeColor: '#10b981',
    strokeEnabled: true,
    strokeColor: '#000000',
    strokeWidth: 4,
    glowEnabled: false,
    glowColor: '#10b981',
    glowBlur: 10,
    visualizerEnabled: false,
    visualizerStyle: 'bars',
    animationPreset: 'pop-in', // none, pop-in, fade
  });
  
  const [currentTime, setCurrentTime] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [duration, setDuration] = useState(0);
  const [isExporting, setIsExporting] = useState(false);
  const [showDocs, setShowDocs] = useState(false);

  const wavesurferRef = useRef(null);
  const canvasRef = useRef(null);
  const audioContextRef = useRef(null);
  const mediaElementSourceRef = useRef(null);
  const audioDestRef = useRef(null); // For exporting
  const analyserRef = useRef(null); // For audio visualizer

  const handlePlayPause = useCallback(() => {
    if (wavesurferRef.current) {
      wavesurferRef.current.playPause();
    }
  }, []);

  const handleExport = async () => {
    if (!audioUrl || !canvasRef.current || !wavesurferRef.current) return;
    
    // Check if bgMedia is video and is ready
    if (bgMedia.type === 'video' && bgMedia.element) {
      bgMedia.element.currentTime = 0;
    }
    
    setIsExporting(true);
    try {
      await exportVideo(
        canvasRef.current,
        wavesurferRef.current,
        audioContextRef.current,
        audioDestRef.current,
        duration,
        bgMedia,
        lyrics,
        styles
      );
    } catch (e) {
      console.error('Export failed:', e);
      alert('Export failed. Check console for details.');
    }
    setIsExporting(false);
  };

  return (
    <div className="h-screen w-screen bg-gray-900 text-white flex flex-col overflow-hidden">
      {/* Topbar */}
      <header className="h-14 border-b border-gray-800 bg-gray-950 flex items-center justify-between px-6 shrink-0">
        <div className="flex items-center gap-4">
          <h1 className="font-bold text-xl text-emerald-400">LyricCanvas</h1>
          <button 
            onClick={() => setShowDocs(true)}
            className="text-sm text-gray-400 hover:text-white underline"
          >
            Architecture & Sync Docs
          </button>
        </div>
        <div className="flex items-center gap-3">
          <button 
            onClick={handleExport}
            disabled={!audioUrl || isExporting}
            className="bg-emerald-600 hover:bg-emerald-500 disabled:opacity-50 disabled:cursor-not-allowed text-white px-4 py-1.5 rounded-md font-medium text-sm transition-colors"
          >
            {isExporting ? 'Exporting...' : 'Export Video'}
          </button>
        </div>
      </header>

      {/* Main Workspace */}
      <div className="flex-1 flex overflow-hidden">
        {/* Left: Media & Import */}
        <aside className="w-64 border-r border-gray-800 bg-gray-950 flex flex-col shrink-0">
          <MediaBin 
            setAudioUrl={setAudioUrl} 
            setBgMedia={setBgMedia}
            setLyrics={setLyrics}
            bgMedia={bgMedia}
          />
        </aside>

        {/* Center: Canvas Preview */}
        <main className="flex-1 bg-gray-900 relative flex items-center justify-center p-8 overflow-hidden">
           <CanvasPreview 
             ref={canvasRef}
             currentTime={currentTime}
             bgMedia={bgMedia}
             lyrics={lyrics}
             styles={styles}
             isPlaying={isPlaying}
             width={1280}
             height={720}
             analyserRef={analyserRef}
           />
           {isExporting && (
             <div className="absolute inset-0 bg-black/80 flex items-center justify-center z-50">
               <div className="text-center">
                 <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-500 mx-auto mb-4"></div>
                 <p className="text-xl font-bold">Exporting Video...</p>
                 <p className="text-gray-400 mt-2">Please do not switch tabs.</p>
               </div>
             </div>
           )}
        </main>

        {/* Right: Inspector */}
        <aside className="w-72 border-l border-gray-800 bg-gray-950 overflow-y-auto shrink-0">
          <Inspector styles={styles} setStyles={setStyles} />
        </aside>
      </div>

      {/* Bottom: Timeline */}
      <div className="h-64 border-t border-gray-800 bg-gray-950 shrink-0 flex flex-col">
        <Timeline 
          audioUrl={audioUrl}
          lyrics={lyrics}
          setLyrics={setLyrics}
          currentTime={currentTime}
          setCurrentTime={setCurrentTime}
          isPlaying={isPlaying}
          setIsPlaying={setIsPlaying}
          duration={duration}
          setDuration={setDuration}
          wavesurferRef={wavesurferRef}
          audioContextRef={audioContextRef}
          mediaElementSourceRef={mediaElementSourceRef}
          audioDestRef={audioDestRef}
          analyserRef={analyserRef}
          onPlayPause={handlePlayPause}
        />
      </div>

      {showDocs && <DocsModal onClose={() => setShowDocs(false)} />}
    </div>
  );
}